import {
  AlertWarningIcon,
  Button,
  EmptyStateIcon,
  MinimalCheckboxTable,
  Modal,
  ModalContent,
  RadioButtonCard,
  TextInput,
  Timing,
} from '@spoiler-alert/ui-library';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { createUseStyles } from 'react-jss';
import Tracking from '../../utilities/tracking-apis';
import contactModalStyles from './contact-modal-styles';
import contactTypes from './contact-types';
import possibleValues from './possible-values';

const useStyles = createUseStyles(contactModalStyles);
const emailRegex =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const EditContactModal = ({
  onHide,
  open,
  handleSubmit,
  loading,
  distributionLists,
  onChange,
  email,
  selectedContactType,
  match,
  setDeleteContactModalOpen,
  distributionListsUpdated,
}) => {
  const classes = useStyles();
  const [emailAddress, setEmailAddress] = useState(email);
  const [contactType, setContactType] = useState(selectedContactType);
  const [emailError, setEmailError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    setEmailAddress(email);
    setContactType(selectedContactType);
  }, [email, selectedContactType]);

  const cancel = () => {
    onHide(false);
    setEmailAddress(email);
    setContactType(selectedContactType);
    setEmailError(false);
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (contactType.toLowerCase() === 'customer') Tracking.Event('Edited Customer Contact');
    if (contactType.toLowerCase() === 'internal') Tracking.Event('Edited Internal Contact');
    const error = await handleSubmit(emailAddress, contactType, distributionLists, email);
    if (error?.code === 'DUPLICATE_EMAIL') {
      setErrorMessage('Duplicate email. Email must be unique within your organization.');
      setEmailError(true);
    }
  };

  const handleChange = (newEmail) => {
    const emailMatch = emailRegex.test(newEmail);
    if (!emailMatch) setErrorMessage('Please enter a valid email address');
    setEmailError(!emailMatch);
    setEmailAddress(newEmail);
  };

  const handleDeleteFromEdit = () => {
    setDeleteContactModalOpen(true);
  };

  return (
    <Modal onHide={onHide} open={open} closeOnEsc closeOnOutsideClick>
      <form onSubmit={submit} className={classes.form__container}>
        <ModalContent>
          <div data-testid="modal-title" className={classes.modal__title}>
            Edit Contact
          </div>
          <div data-testid="modal-body" className={classes.modal__body}>
            <div className={`${classes.row} ${classes.email}`}>
              <TextInput labelText="Email Address *" value={emailAddress} onChange={Timing.debounce(handleChange, 500)} required error={emailError} />
              {emailError && (
                <span className={classes.errorMessage}>
                  <AlertWarningIcon className={classes.warningIcon} />
                  {errorMessage}
                </span>
              )}
            </div>
            <div className={classes.row}>
              <span className={classes.contactType}>Contact Type *</span>
              <RadioButtonCard options={contactTypes} selected={contactType} onChange={(option) => setContactType(option.value)} />
            </div>
            <div className={classes.tableTitle}>Distribution lists this contact is included in</div>
            <div>
              {(distributionLists.length > 0 && (
                <MinimalCheckboxTable data={distributionLists} possibleValues={possibleValues} onChange={onChange} keyHeader={'Distribution List'} />
              )) || (
                <div className={classes.zeroStateContainer}>
                  <span className={classes.zeroStateInfo}>Add contact to distribution lists</span>
                  <div className={classes.zeroStateIconContainer}>
                    <EmptyStateIcon />
                    <span className={classes.zeroStateText}>{match.params.buyerSiteName} hasn’t been added to any distribution lists yet.</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </ModalContent>
        <div className={`${classes.footer__container} ${classes.footer__container_space_between}`}>
          <div>
            <Button className={`${classes.deleteButton} ${classes.deleteButtonNoBorder}`} type="button" onClick={handleDeleteFromEdit} secondary>
              Delete Contact
            </Button>
          </div>
          <div className={classes.modal__submit}>
            <Button testId="cancel" className={classes.modal__cancel} type="button" onClick={cancel} secondary disabled={loading}>
              Cancel
            </Button>
            <Button
              className={classes.actionButton}
              testId="submit"
              type="submit"
              loadingText={'Saving Contact'}
              loading={loading}
              disabled={
                !emailAddress ||
                emailError ||
                !contactType ||
                (emailAddress === email && contactType === selectedContactType && !distributionListsUpdated)
              }
            >
              Save Changes
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
};

EditContactModal.propTypes = {
  onHide: PropTypes.func,
  open: PropTypes.bool,
  handleSubmit: PropTypes.func,
  loading: PropTypes.bool,
  distributionLists: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
      key: PropTypes.string,
      value: PropTypes.array,
    }),
  ),
  onChange: PropTypes.func,
  email: PropTypes.string,
  selectedContactType: PropTypes.string,
  match: PropTypes.object,
  setDeleteContactModalOpen: PropTypes.func,
  setEditContactModalOpen: PropTypes.func,
  distributionListsUpdated: PropTypes.bool,
};

export default EditContactModal;
